/**
 * Play post office — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_tickets_play`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const playPostOfficePack: ToilePack = {
    slug: 'play-post-office',
    category: 'classroom',
    name: 'Play post office',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'stamps', label: 'Postage stamps', subject: 'a sheet of twenty postage stamps with perforated edges, each with a small animal or flower and a value printed in the corner, labelled "POSTAGE"'},
        {key: 'envelope', label: 'Envelope', subject: 'an envelope template with dashed fold lines and flaps, a stamp box in the corner and blank lines for the address under "TO"'},
        {key: 'parcel', label: 'Parcel form', subject: 'a parcel delivery form with blank fields for sender, receiver and weight, a barcode strip and "POST OFFICE" across the top'},
    ],
    styles: [
        {key: 'vintage', label: 'Vintage post', style: 'a vintage postal design with engraved stamp borders and postmark circles', palette: 'sepia, navy and red on cream', framing: 'the piece centered on the page with a wide cream margin'},
        {key: 'kid', label: 'Kids post', style: 'a bright cartoon design with rounded animals and chunky letters', palette: 'primary red, blue and yellow on white', framing: 'the piece centered on the page with a wide white margin'},
        {key: 'mono', label: 'Black and white', style: 'a simple black line print with large clear shapes and plain lettering', palette: 'black ink only', framing: 'the piece centered on the page with a wide white margin'},
    ],
    directions: ['airmail', 'christmas post', 'animal stamps'],
    directionHint: 'try: airmail',
    seo: {
        title: 'Pretend Post Office Printables: Stamps, Envelope, Parcel',
        metaDescription: 'Nine printable post office play pieces: a sheet of stamps, an envelope template and a parcel form, in three styles.',
        h1: 'Pretend post office printables: postage stamps, envelope, parcel form',
        tagline: 'Nine post office pieces, three kinds of postal paper, each in a vintage, kids or black-and-white style.',
        intro: 'Children write letters, stamp them and deliver them in a pretend post office. The printable pieces are a sheet of postage stamps, an envelope template and a parcel form. The nine pictures show stamps with animals and flowers, a dashed envelope with a stamp box, and a parcel form with a barcode strip, in sepia-and-navy vintage, bright cartoon and plain black line. Type a different theme, such as airmail, and the pieces follow.',
        useCases: [
            'a pretend post office in a preschool',
            'a letter-writing activity at home',
            'a classroom unit on addresses',
            'a delivery game with parcels',
        ],
        faq: [
            {q: 'What is on the stamps?', a: 'Each stamp is drawn with a perforated edge and shows a small animal or flower, with a value printed in the corner.'},
            {q: 'Where does the address go on the envelope?', a: 'The envelope template has a stamp box in the corner and blank lines for the address under TO.'},
            {q: 'What is the difference between the three styles?', a: 'Vintage post is sepia, navy and red on cream, with engraved stamp borders and postmark circles. Kids post is bright red, blue and yellow with rounded animals. Black and white is a simple black line print.'},
        ],
    },
}
